import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loadCookie, SasCookieKeys } from 'common/utils/cookie-utils';

import { BoardStatus, boardState, setStatus } from 'common/modules/app/board';
import { GlossaryState } from 'common/modules/app/glossaries';
import { Glossary } from 'common/utils/glossary';
import { FetchState, FetchStatus } from 'common/modules/app/fetch';
import {
  TerminalState,
  TerminalMap,
  deleteTerminalActivation,
  DELETE_TERMINAL_ACTIVATION_ID
} from 'common/modules/domain/terminal';
import {
  reset,
  getPurchasesHistory,
  StateType,
  SortType
} from 'common/modules/domain/purchase';
import { UserInformation } from 'common/modules/domain/user';
import { AppState } from 'pages';
import { TerminalDeleteDialog } from 'pages/board/components/TerminalDeleteDialog';
import { RouterState } from 'connected-react-router';

/**
 * StoreのStateからComponentに渡すPropsへ
 */
const useStateProps = () => {
  const boardState = useSelector<AppState, boardState>(
    (state) => state.app.board
  );
  const glState = useSelector<AppState, GlossaryState>(
    (state) => state.app.glossaries
  );
  const userState = useSelector<AppState, UserInformation>(
    (state) => state.domain.user
  );
  const terminalState = useSelector<AppState, TerminalState>(
    (state) => state.domain.terminal
  );
  const fetchState = useSelector<AppState, FetchState>(
    (state) => state.app.fetch
  );
  const routerState = useSelector<AppState, RouterState>(
    (state) => state.app.router
  );

  const isDeleteProcessing =
    fetchState[DELETE_TERMINAL_ACTIVATION_ID] !== null &&
    fetchState[DELETE_TERMINAL_ACTIVATION_ID] === FetchStatus.PROCESSING;

  return {
    board: boardState,
    glossary: Glossary.instance(glState),
    user: userState,
    isDeleteProcessing: isDeleteProcessing,
    terminal: terminalState,
    router: routerState
  };
};

type SelectedState = ReturnType<typeof useStateProps>;
/**
 * StoreのStateとActionをdispatchする関数からComponentに渡すPropsへ
 */
const useMargeProps = (state: SelectedState) => {
  const dispatch = useDispatch();
  const _board = state.board;
  /** TerminalDeleteDialogの表示有無 */
  const _isDisplay =
    _board.status === BoardStatus.TERMINAL_CONFIRMATION_DISP ||
    _board.status === BoardStatus.TERMINAL_SUCCESS_DISP ||
    _board.status === BoardStatus.TERMINAL_FAILURE_DISP;
  /** SASトークン取得 */
  const cookie = loadCookie();
  const _token = cookie[SasCookieKeys.KEY_TOKEN];
  const _language = state.user.language;
  const _from = '';
  /** 削除対象の端末IDリスト生成 */
  const _terminal = state.terminal;
  const terminalMap: TerminalMap =
    _terminal[_board.selectedPurchaseIdForTerminal];
  const terminalIds: string[] = [];
  if (terminalMap != null) {
    for (const terminalId of Object.keys(terminalMap)) {
      if (terminalMap[terminalId].isDelete == true) {
        terminalIds.push(terminalId);
      }
    }
  }

  /** "OKボタン"の押下処理(確認ダイアログ) */
  const onClickDelete = () => {
    // 端末削除APIを実行
    dispatch(
      deleteTerminalActivation(
        _token,
        _board.selectedPurchaseIdForTerminal,
        terminalIds
      )
    );
  };

  /**
   * 確認ダイアログ："キャンセルボタン"の押下処理
   * 成功/失敗ダイアログ："OKボタン"の押下処理
   **/
  const onClickOkClose = async () => {
    // 成功ダイアログの場合のみ購入履歴の再取得を行う
    if (_board.status === BoardStatus.TERMINAL_SUCCESS_DISP) {
      await dispatch(reset());
      await dispatch(
        getPurchasesHistory(
          _token,
          _language,
          StateType.TYPE_ALL,
          SortType.TYPE_PURCHASE_ID,
          _from
        )
      );
    }
    dispatch(setStatus(BoardStatus.TERMINAL_DISP));
  };

  return {
    isDisplay: _isDisplay,
    boardStatus: _board.status,
    glossary: state.glossary,
    isDeleteProcessing: state.isDeleteProcessing,
    onClickDelete: onClickDelete,
    onClickOkClose: onClickOkClose
  };
};

/**
 * 端末管理画面全体のContainer Component
 */
export default function ContainerComponent(ownProps: { isMobile?: boolean }) {
  const _props = { ...useStateProps(), ...useMargeProps(useStateProps()) };
  return <TerminalDeleteDialog {...{ ..._props, ...ownProps }} />;
}
